// @ts-nocheck
// @ts-ignore
import React, { useState, useEffect, useCallback, useContext, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import ReactGridLayout, { WidthProvider, Layout } from "react-grid-layout/legacy";
import { Link } from "react-router-dom";
import { Trans } from "react-i18next";

import { ReducerState } from "reducers";

import { PanelState } from "types/Dashboard";
import { ComplexComponent, TypeComplexComponent } from "enum";

import { closePanel, layoutChange } from "actions/dashboard";

import Panel from "./Panel";

import { EmptyDashboard } from "./emptyDashboard";
import PanelLight from "./PanelLight";

import { InformationProvider, InformationRefresh } from "containers/information/Information";
import { LoadableLoader } from "composants/Loader";
import { Fa } from "composants/Icon";
import { createSelector } from "reselect";
import { InformationTemplate } from "composants/genericDisplay/ServerTemplateComponent";
import { initRsqlCriteria } from "utils/query.utils";
import { RSQLFilterExpression, Operators } from "rsql-criteria-typescript";
import { find } from "api";
import { ErrorBoundary } from "composants/ErrorBoundary";
import { SuperUserContext } from "composants/userSettings/SuperUser";

type AdminButtonProps = { panel: PanelState; tableName: string };
const AdminButton = React.memo(function AdminButton({ panel, tableName }: AdminButtonProps) {
  const [url, setUrl] = useState<string | "UNKNOWN">(getURL);

  function getURL() {
    let url;
    if (panel.panelType === TypeComplexComponent.DATATABLE) {
      url = `/admin/integrateur/datatable/${panel.panelTableName}`;
    } else if (panel.panelType === TypeComplexComponent.INTERACTIVE_REPORT) {
      url = `/admin/integrateur/interactivereport/${panel.interactiveReportId}`;
    } else if (panel.panelType === TypeComplexComponent.NAV_CARDS) {
      url = `/admin/integrateur/navcard/${panel.panelId}`;
    } else if (panel.panelType === TypeComplexComponent.SAS) {
      url = `/admin/integrateur/sas/${panel.panelId}`;
    } else if (panel.panelType === TypeComplexComponent.INDICATEUR) {
      url = `/admin/integrateur/indicateur/${panel.panelId}`;
    } else if (panel.panelType === TypeComplexComponent.ARBO) {
      url = `/admin/integrateur/arborescence/${panel.panelId}`;
    } else if (panel.panelType === TypeComplexComponent.AGGRID) {
      url = `/admin/integrateur/datatable/${panel.panelTableName}`;
    } else if (panel.panelType === TypeComplexComponent.IR_AG) {
      url = `/admin/integrateur/interactivereport/${panel.interactiveReportId}`;
    }

    return url || "UNKNOWN";
  }

  async function getMySyjModuleId(syjPanelId: string): Promise<string | null> {
    const rsql = initRsqlCriteria();
    rsql.filters.and(new RSQLFilterExpression("id", Operators.Equal, syjPanelId));
    try {
      const res = await find("syjPanel", rsql.build());
      if (res.data.data.length > 0) {
        return res.data.data[0].syjModuleId;
      } else {
        return null;
      }
    } catch (error) {
      console.error(`Error getting syjModuleId for syjPanelId ${syjPanelId}:`, error);
      return null;
    }
  }

  async function onAccessToAdminDgl(panel: PanelState) {
    let url = "";
    const syjModuleId = await getMySyjModuleId(panel.panelId);
    const rsqlForsyjDglId = initRsqlCriteria();
    rsqlForsyjDglId.filters.and(
      new RSQLFilterExpression("syjPanelId.id", Operators.Equal, panel.panelId)
    );
    if (syjModuleId !== null) {
      find("syjPanelDgList", rsqlForsyjDglId.build()).then(res => {
        if (res.data.data.length > 0) {
          url = `/admin/integrateur/dgl/${syjModuleId}/${res.data.data[0].syjPanelId}/${res.data.data[0].syjDgListId}`;
        } else {
          url = `/admin/integrateur/dgl`;
        }
        setUrl(url);
      });
    } else {
      url = `/admin/integrateur/dgl`;
      setUrl(url);
    }
  }

  function onAccessToMiniExpert(tableName: string) {
    let url = "";
    const rsql = initRsqlCriteria();
    rsql.filters.and(new RSQLFilterExpression("sjtaName", Operators.Equal, tableName));
    find("syjTables", rsql.build()).then(res => {
      if (res.data.data.length > 0) {
        url = `/admin/integrateur/miniexpert/${res.data.data[0].id}`;
      } else {
        url = `/admin/integrateur/miniexpert`;
      }
      setUrl(url);
    });
  }

  useEffect(() => {
    if (panel.panelType === TypeComplexComponent.DGL) {
      onAccessToAdminDgl(panel);
    } else if (panel.panelType === TypeComplexComponent.MINI_EXPERT) {
      onAccessToMiniExpert(tableName);
    }
  }, [panel]);

  return (
    <>
      {url === "UNKNOWN" ? (
        <Link to={url} target="_blank">
          <span className="icon">
            <Fa icon="spinner" />
          </span>
        </Link>
      ) : (
        <Link to={url} target="_blank">
          <span className="icon">
            <Fa icon="cog" />
          </span>
        </Link>
      )}
    </>
  );
});
const ResponsiveReactGridLayout = WidthProvider(ReactGridLayout);

const PANEL_TYPES_WITH_LEFT_HEADER = [
  TypeComplexComponent.DATATABLE,
  TypeComplexComponent.MINI_EXPERT,
  TypeComplexComponent.INTERACTIVE_REPORT,
  TypeComplexComponent.IR_AG,
  TypeComplexComponent.NAV_CARDS,
  TypeComplexComponent.DGL,
  TypeComplexComponent.SAS,
  TypeComplexComponent.INDICATEUR,
  TypeComplexComponent.ARBO,
  TypeComplexComponent.PLANIF,
  TypeComplexComponent.AGGRID
];
const PANEL_TYPES_WITH_OVERFLOW = ["INFORMATION", "ARBO", "MINI_EXPERT", "EDITOR", "PLANIF"];
const PANEL_TYPES_WITH_NO_PADDING = ["DT", "LISTE_GENERIQUE", "INTERACTIVE_REPORT", "AGGRID", "IR_AG"];

interface PanelGridProps {
  sjmoCode: string;
  tableName: string;
  currentMainEntityId: string | null;
  onAfterSaveDatatable(): void;
  onAfterDeleteDatatable(): void;
  onAfterProcessListeGeneric(): void;
  onOpenCreator?(creatorTableName: string, creatorSelectedFocus?: string | null): void;
}

const selectDashboardDefinition = (state: ReducerState, sjmoCode: string) =>
  state.dashboard.dashboardDefinitions[sjmoCode];

const selectSelectedFocus = (state: ReducerState, sjmoCode: string) =>
  state.dashboard.selected[sjmoCode];

const selectIndexActiveFocus = (state: ReducerState, sjmoCode: string, focusId: string) => {
  const def = state.dashboard.dashboardDefinitions[sjmoCode];
  return def ? def.findIndex(el => el.focusId === focusId) : null;
};

type PanelsAndFocusIdPropsSelector = { sjmoCode: string };
const selectPanelsAndFocus = createSelector(
  (state: ReducerState, props: PanelsAndFocusIdPropsSelector) =>
    selectDashboardDefinition(state, props.sjmoCode),
  (state: ReducerState, props: PanelsAndFocusIdPropsSelector) =>
    selectSelectedFocus(state, props.sjmoCode),
  (currentModuleDefinitions, selectedFocus) => {
    const currentFocus = currentModuleDefinitions.find(d => d.focusId === selectedFocus);
    if (currentFocus) {
      const orderedPanels = currentFocus.panels.slice();
      orderedPanels.sort((a, b) => (a.posY < b.posY || a.posX < b.posX ? -1 : 1));

      return { panels: orderedPanels, focusId: selectedFocus };
    }
    return { panels: [], focusId: selectedFocus };
  }
);

function PanelGrid({
  sjmoCode,
  tableName,
  currentMainEntityId,
  onAfterSaveDatatable,
  onAfterDeleteDatatable,
  onAfterProcessListeGeneric,
  onOpenCreator
}: PanelGridProps) {
  const dispatch = useDispatch();
  const { adminLevel, superUser } = useContext(SuperUserContext);
  const { panels, focusId } = useSelector((state: ReducerState) =>
    selectPanelsAndFocus(state, { sjmoCode })
  );

  const onLayoutChange = useCallback(
    (layouts: Layout[]) => {
      dispatch(layoutChange(sjmoCode, layouts));
    },
    [dispatch, sjmoCode]
  );

  const onPanelClose = useCallback(
    (sjpaId: string) => {
      dispatch(closePanel(sjmoCode, sjpaId));
    },
    [dispatch, sjmoCode]
  );

  // Mémoïsé pour ne pas reconstruire tous les panels (et casser le rendu du
  // grid drag & drop de react-grid-layout) à chaque re-render non lié au dashboard.
  const listPanel = useMemo(() => {
    return panels
      .filter(panel => panel.visible)
      .map(panel => {
        const SelectedComponent =
          ComplexComponent[panel.panelType as keyof typeof ComplexComponent];
        const layout = {
          i: `${focusId}_${panel.panelId}`,
          x: panel.posX,
          y: panel.posY,
          w: panel.width,
          h: panel.height
        };

        const buttonAdmin = PANEL_TYPES_WITH_LEFT_HEADER.indexOf(panel.panelType) !== -1 &&
          adminLevel === 3 &&
          superUser && (
            <AdminButton panel={panel} tableName={tableName} />
          );

        switch (panel.panelType) {
          case TypeComplexComponent.NAV_CARDS:
          case TypeComplexComponent.INDICATEUR:
            return (
              <section
                key={layout.i}
                data-grid={layout}
                style={{ padding: 2 }}
                aria-label={panel.panelLabel}
              >
                <PanelLight
                  onPanelClose={onPanelClose}
                  style={{ height: "inherit" }}
                  noPaddingContent={PANEL_TYPES_WITH_NO_PADDING.indexOf(panel.panelType) !== -1}
                  isOverflowScroll={PANEL_TYPES_WITH_OVERFLOW.indexOf(panel.panelType) !== -1}
                  panelId={panel.panelId}
                  leftHeader={<>{buttonAdmin}</>}
                  mandatory={panel.mandatory}
                >
                  <ErrorBoundary>
                    {SelectedComponent ? (
                      <SelectedComponent
                        sjmoCode={sjmoCode}
                        ctrlkey={undefined}
                        sjpaId={panel.panelId}
                        currentMainEntityId={currentMainEntityId}
                      />
                    ) : null}
                  </ErrorBoundary>
                </PanelLight>
              </section>
            );

          case TypeComplexComponent.INFORMATION:
            return (
              <section
                key={layout.i}
                data-grid={layout}
                style={{ padding: 2 }}
                aria-label={panel.panelLabel}
              >
                {import.meta.env.VITE_FLAG_NEW_TEMPLATE === "true" &&
                  panel.informationTemplateName ? (
                  <Panel
                    title={panel.panelLabel}
                    onPanelClose={onPanelClose}
                    style={{ height: "inherit", overflow: "hidden" }}
                    noPaddingContent={PANEL_TYPES_WITH_NO_PADDING.indexOf(panel.panelType) !== -1}
                    isOverflowScroll={PANEL_TYPES_WITH_OVERFLOW.indexOf(panel.panelType) !== -1}
                    panel={panel}
                    leftHeader={<>{buttonAdmin}</>}
                    onOpenCreator={onOpenCreator}
                  >
                    <ErrorBoundary>
                      <InformationTemplate
                        moduleCode={sjmoCode}
                        panelId={panel.panelId}
                        entityId={currentMainEntityId}
                        templateIdentifier={panel.informationTemplateName}
                      />
                    </ErrorBoundary>
                  </Panel>
                ) : (
                  <InformationProvider
                    currentMainEntityId={currentMainEntityId}
                    sjmoCode={sjmoCode}
                    sjpaId={panel.panelId}
                  >
                    <Panel
                      title={panel.panelLabel}
                      onPanelClose={onPanelClose}
                      style={{ height: "inherit", overflow: "hidden" }}
                      noPaddingContent={PANEL_TYPES_WITH_NO_PADDING.indexOf(panel.panelType) !== -1}
                      isOverflowScroll={PANEL_TYPES_WITH_OVERFLOW.indexOf(panel.panelType) !== -1}
                      panel={panel}
                      leftHeader={
                        <>
                          {buttonAdmin}
                          <InformationRefresh />
                        </>
                      }
                      onOpenCreator={onOpenCreator}
                    >
                      <ErrorBoundary>
                        <ComplexComponent.INFORMATION />
                      </ErrorBoundary>
                    </Panel>
                  </InformationProvider>
                )}
              </section>
            );

          default:
            return (
              <section
                key={layout.i}
                data-grid={layout}
                style={{ padding: 2 }}
                aria-label={panel.panelLabel}
              >
                <Panel
                  id={
                    panel.panelType === TypeComplexComponent.DATATABLE
                      ? `panel-${panel.panelCtrlKey}`
                      : undefined
                  }
                  title={panel.panelLabel}
                  onPanelClose={onPanelClose}
                  style={{ height: "inherit" }}
                  noPaddingContent={PANEL_TYPES_WITH_NO_PADDING.indexOf(panel.panelType) !== -1}
                  isOverflowScroll={PANEL_TYPES_WITH_OVERFLOW.indexOf(panel.panelType) !== -1}
                  panel={panel}
                  leftHeader={<>{buttonAdmin}</>}
                  onOpenCreator={onOpenCreator}
                >
                  <ErrorBoundary>
                    {SelectedComponent ? (
                      <SelectedComponent
                        sjmoCode={sjmoCode}
                        ctrlkey={
                          panel.panelType === TypeComplexComponent.DATATABLE ||
                            panel.panelType === TypeComplexComponent.EDITOR ||
                            panel.panelType === TypeComplexComponent.AGGRID
                            ? panel.panelCtrlKey
                            : undefined
                        }
                        sjpaId={panel.panelId}
                        editorColumnIds={panel.editorColumnIds}
                        tableName={
                          [
                            TypeComplexComponent.DATATABLE,
                            TypeComplexComponent.EDITOR,
                            TypeComplexComponent.AGGRID
                          ].indexOf(panel.panelType) !== -1
                            ? panel.panelTableName
                            : tableName
                        }
                        currentMainEntityId={currentMainEntityId}
                        onAfterUpload={
                          panel.panelType === TypeComplexComponent.SAS
                            ? onAfterSaveDatatable
                            : undefined
                        }
                        onAfterSaveDatatable={
                          panel.panelType === TypeComplexComponent.DATATABLE ||
                            panel.panelType === TypeComplexComponent.AGGRID
                            ? onAfterSaveDatatable
                            : panel.panelType === TypeComplexComponent.INTERACTIVE_REPORT || panel.panelType === TypeComplexComponent.IR_AG
                              ? onAfterProcessListeGeneric
                              : undefined
                        }
                        onAfterDeleteDatatable={
                          panel.panelType === TypeComplexComponent.DATATABLE ||
                            panel.panelType === TypeComplexComponent.AGGRID
                            ? onAfterDeleteDatatable
                            : undefined
                        }
                        onAfterProcessListeGeneric={
                          panel.panelType === TypeComplexComponent.DATATABLE ||
                            panel.panelType === TypeComplexComponent.AGGRID
                            ? onAfterProcessListeGeneric
                            : undefined
                        }
                        nodeType={
                          panel.panelType === TypeComplexComponent.DATATABLE ||
                            panel.panelType === TypeComplexComponent.AGGRID
                            ? panel.nodeType
                            : undefined
                        }
                        processIdDrop={
                          panel.panelType === TypeComplexComponent.DATATABLE ||
                            panel.panelType === TypeComplexComponent.AGGRID
                            ? panel.processIdDrop
                            : undefined
                        }
                        definitionId={panel.interactiveReportId}
                      />
                    ) : null}
                  </ErrorBoundary>
                </Panel>
              </section>
            );
        }
      });
  }, [
    panels,
    focusId,
    adminLevel,
    superUser,
    sjmoCode,
    tableName,
    currentMainEntityId,
    onOpenCreator,
    onAfterSaveDatatable,
    onAfterDeleteDatatable,
    onAfterProcessListeGeneric,
    onPanelClose
  ]);

  if (panels.filter(p => p.visible).length <= 0) {
    return (
      <div style={{ textAlign: "center", marginTop: "1em" }}>
        <div className="title is-5">
          <Trans i18nKey="commun_dashboard_vide">Votre dashboard est vide</Trans>
        </div>
        <EmptyDashboard />
        <div className="subtitle is-4">
          <Trans i18nKey="commun_ajouter_un_panel_pour_commencer">
            Ajouter un <strong>panel</strong> pour commencer
          </Trans>
        </div>
      </div>
    );
  }

  return (
    <React.Suspense fallback={<LoadableLoader />}>
      <ResponsiveReactGridLayout
        className="layout"
        cols={12}
        rowHeight={30}
        onLayoutChange={onLayoutChange}
        measureBeforeMount
        draggableHandle=".card-header-draggable"
      >
        {listPanel}
      </ResponsiveReactGridLayout>
    </React.Suspense>
  );
}

export default React.memo(PanelGrid);
